import { useCallback, useEffect, useState } from 'react';
import { Centro } from '../types/centro.types';
import { CentroService } from '../services/CentroService';

const DEBOUNCE_MS = 500;
const MAX_PAGES_TO_SHOW = 5;

export function useCentrosList(proveedorId?: string | number) {
    const [centros, setCentros] = useState<Centro[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortBy, setSortBy] = useState('sucursal');
    const [sortOrder, setSortOrder] = useState('asc');

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1); // nueva búsqueda → volver a página 1
        }, DEBOUNCE_MS);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchCentros = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await CentroService.fetchCentros({
                proveedorId,
                searchTerm: debouncedSearch,
                page,
                limit,
                sortBy: `${sortBy}_${sortOrder}`,
            });

            if (result.status === 'success') {
                setCentros(result.data ?? []);
                setTotalRecords(result.total ?? 0);
            } else {
                setError(result.message ?? 'Error al cargar los centros.');
            }
        } catch {
            setError('Error de red al comunicarse con el servidor.');
        } finally {
            setIsLoading(false);
        }
    }, [debouncedSearch, page, limit, sortBy, sortOrder, proveedorId]);

    useEffect(() => {
        fetchCentros();
    }, [fetchCentros]);

    const handleSort = (column: string) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
        setPage(1);
    };

    const totalPages = Math.ceil(totalRecords / limit);

    const getPageNumbers = (): number[] => {
        let startPage = Math.max(1, page - Math.floor(MAX_PAGES_TO_SHOW / 2));
        let endPage = startPage + MAX_PAGES_TO_SHOW - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - MAX_PAGES_TO_SHOW + 1);
        }

        const pages: number[] = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    return {
        centros,
        isLoading,
        error,
        searchTerm,
        setSearchTerm,
        page,
        setPage,
        limit,
        setLimit,
        totalRecords,
        sortBy,
        sortOrder,
        totalPages,
        fetchCentros,
        handleSort,
        getPageNumbers,
    };
}
